import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface MentalModel {
  name: string;
  nameEn: string;
  icon: string;
  core: string;
  application: string;
  field: string;
  color: string;
}

const MODELS: MentalModel[] = [
  {
    name: '逆向思维',
    nameEn: 'Inversion',
    icon: '🔄',
    core: '与其想如何成功，不如想如何避免失败',
    application: '查理·芒格："反过来想，总是反过来想"',
    field: '决策 / 投资',
    color: '#FF6B35',
  },
  {
    name: '二八法则',
    nameEn: 'Pareto Principle',
    icon: '📊',
    core: '80%的结果来自20%的原因',
    application: '聚焦最重要的20%工作，产出80%价值',
    field: '效率 / 管理',
    color: '#4ECDC4',
  },
  {
    name: '奥卡姆剃刀',
    nameEn: "Occam's Razor",
    icon: '🪒',
    core: '如无必要，勿增实体 — 最简单的解释往往是正确的',
    application: '面对多个假说时，优先选择假设最少的那个',
    field: '科学 / 逻辑',
    color: '#667EEA',
  },
  {
    name: '能力圈',
    nameEn: 'Circle of Competence',
    icon: '🎯',
    core: '明确自己的能力边界，在能力圈内做决策',
    application: '巴菲特只投资自己能理解的行业',
    field: '投资 / 认知',
    color: '#FFD700',
  },
  {
    name: '地图不是领土',
    nameEn: 'Map ≠ Territory',
    icon: '🗺️',
    core: '模型是对现实的简化，不要把模型当作现实本身',
    application: '财务报表只是企业的抽象表示，不是企业本身',
    field: '认知 / 哲学',
    color: '#9B59B6',
  },
  {
    name: '复利效应',
    nameEn: 'Compound Effect',
    icon: '📈',
    core: '微小的持续改进会产生巨大的累积效果',
    application: '每天进步1%，一年后你将是起初的37.8倍',
    field: '成长 / 投资',
    color: '#2ECC71',
  },
];

export const MentalModelAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleProg = spring({ frame, fps, config: { damping: 14, stiffness: 120 } });

  const currentIdx = Math.min(
    Math.floor(Math.max(0, frame - 50) / 80),
    MODELS.length - 1,
  );

  return (
    <AbsoluteFill
      style={{
        background: 'radial-gradient(ellipse at 50% 40%, #111827 0%, #0B0E17 100%)',
        fontFamily: 'system-ui, -apple-system, PingFang SC, sans-serif',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 36,
          left: 55,
          opacity: titleProg,
          transform: `translateY(${interpolate(titleProg, [0, 1], [-30, 0])}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 64,
            fontWeight: 800,
            margin: 0,
            background: 'linear-gradient(135deg, #FF6B35, #FFD700, #4ECDC4)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          核心心智模型
        </h1>
        <p style={{ fontSize: 28, margin: '6px 0 0', color: 'rgba(255,255,255,0.4)' }}>
          Mental Models · 用多元思维模型理解世界
        </p>
      </div>

      {/* Model grid on the left as a vertical list */}
      <div
        style={{
          position: 'absolute',
          top: 240,
          left: 55,
          width: 260,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {MODELS.map((model, i) => {
          const mProg = spring({
            frame: frame - 12 - i * 6,
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          const isActive = i === currentIdx;

          return (
            <div
              key={model.nameEn}
              style={{
                padding: '10px 16px',
                background: isActive ? `${model.color}18` : 'rgba(255,255,255,0.02)',
                border: `2px solid ${isActive ? model.color + '50' : 'rgba(255,255,255,0.06)'}`,
                borderRadius: 12,
                opacity: mProg,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <span style={{ fontSize: 30 }}>{model.icon}</span>
              <span
                style={{
                  fontSize: 30,
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? model.color : 'rgba(255,255,255,0.4)',
                }}
              >
                {model.name}
              </span>
            </div>
          );
        })}
      </div>

      {/* Detail panel */}
      {MODELS.map((model, i) => {
        const phaseStart = 50 + i * 80;
        const phaseEnd = phaseStart + 75;
        const fadeIn = interpolate(frame, [phaseStart, phaseStart + 16], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });
        const fadeOut = interpolate(frame, [phaseEnd - 12, phaseEnd], [1, 0], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });
        const opacity = i === MODELS.length - 1 ? fadeIn : fadeIn * fadeOut;

        if (frame < phaseStart || opacity <= 0) return null;

        return (
          <div
            key={model.nameEn}
            style={{
              position: 'absolute',
              top: 240,
              left: 340,
              right: 55,
              opacity,
            }}
          >
            <div
              style={{
                padding: '40px 44px',
                background: `${model.color}06`,
                border: `2px solid ${model.color}30`,
                borderRadius: 24,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
                <span style={{ fontSize: 56 }}>{model.icon}</span>
                <div>
                  <h2 style={{ fontSize: 48, fontWeight: 700, color: model.color, margin: 0 }}>
                    {model.name}
                  </h2>
                  <span style={{ fontSize: 28, color: 'rgba(255,255,255,0.35)' }}>
                    {model.nameEn}
                  </span>
                </div>
                <div
                  style={{
                    marginLeft: 'auto',
                    padding: '6px 18px',
                    background: `${model.color}15`,
                    borderRadius: 10,
                    fontSize: 28,
                    color: model.color,
                    fontWeight: 600,
                  }}
                >
                  {model.field}
                </div>
              </div>

              <div
                style={{
                  padding: '24px 30px',
                  background: 'rgba(0,0,0,0.2)',
                  borderRadius: 16,
                  marginBottom: 24,
                  borderLeft: `6px solid ${model.color}`,
                }}
              >
                <p style={{ fontSize: 30, color: '#fff', margin: 0, lineHeight: 1.5, fontWeight: 600 }}>
                  {model.core}
                </p>
              </div>

              <div
                style={{
                  padding: '20px 28px',
                  background: `${model.color}08`,
                  borderRadius: 14,
                  border: `1px solid ${model.color}20`,
                }}
              >
                <div style={{ fontSize: 28, color: 'rgba(255,255,255,0.35)', marginBottom: 8 }}>
                  💡 应用实例
                </div>
                <p style={{ fontSize: 28, color: '#D1D5DB', margin: 0, lineHeight: 1.6 }}>
                  {model.application}
                </p>
              </div>
            </div>
          </div>
        );
      })}

      <div
        style={{
          position: 'absolute',
          bottom: 32,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          gap: 10,
        }}
      >
        {MODELS.map((m, i) => (
          <div
            key={m.nameEn}
            style={{
              width: i === currentIdx ? 36 : 10,
              height: 10,
              borderRadius: 5,
              background: i === currentIdx ? m.color : 'rgba(255,255,255,0.12)',
            }}
          />
        ))}
      </div>
    </AbsoluteFill>
  );
};
